import { State } from 'history';
import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { BaseEntite } from '../../../../../common/src/entities';
import {
    estVide,
    RequeteRechercheApi,
    ReponseRechercheApi,
    RECHERCHE_API,
    reponseVide,
} from '../../../../../common/src/interfaces/rechercheApi';
import api from '../../../Utils/api';

const TITLE = 'Résultats pour';
const FAIRE_RECHERCHE_MESSAGE = 'Faites une recherche dans les données du dashboard';
const AUCUN_RESULTAT = 'Aucun résultat trouvé';

/**
 * Vue de la page de recherche dans le dashboard
 * @returns le html de la page
 */
function PageRecherche(): ReactElement {
    const [urlSearchParams] = useSearchParams();
    const [requete, setRequete] = useState<string | null>(null);
    const [resultatsRecherche, setResultatsRecherche] = useState<ReponseRechercheApi>(reponseVide);
    const [liens, setLiens] = useState<string[]>([]);

    /**
     * Prendre les params retrouvé dans l'adresse URL au chargement de la page
     */
    useEffect(() => {
        setRequete(urlSearchParams.get('requete')); // requete provient de RequeteRechercheApi
    }, [urlSearchParams]);

    /**
     * Faire une requete API au chargement de la page des mots à rechercher
     */
    useEffect(() => {
        if (!requete) return;

        const apiRequete: RequeteRechercheApi = {
            requete: requete.trim(),
        };

        api.get(RECHERCHE_API, { params: apiRequete })
            .then((res) => res.data)
            .then((data) => setResultatsRecherche(data))
            .catch((err) => console.error(err));
    }, [requete]);

    /**
     * Après avoir obtenu les résultats de la requete, creer les liens qui
     * seront affiché à l'usager. Quand l'usager clique sur un résultat, il est
     * envoyé au formulaire correspondant.
     */
    useEffect(() => {
        gererLiens();
    }, [resultatsRecherche]);

    /**
     * Crée les liens de redirection vers les formulaires lorsque l'usager clique sur
     * un des résultats. Les liens dépendent de l'id et de l'entité.
     */
    function gererLiens(): void {
        const listeLiens = [
            ...resultatsRecherche.fournisseurs.map((el) => `/dashboard/fournisseurs/${el.id}`),
            ...resultatsRecherche.activites.map((el) => `/dashboard/activites/${el.id}`),
            ...resultatsRecherche.clients.map((el) => `/dashboard/clients/${el.id}`),
            ...resultatsRecherche.contacts.map((el) => `/dashboard/contacts/${el.id}`),
            ...resultatsRecherche.lieux.map((el) => `/dashboard/lieux/${el.id}`),
            ...resultatsRecherche.centresSS.map((el) => `/dashboard/centres-service-scolaire/${el.id}`),
        ];

        setLiens(listeLiens);
    }

    /**
     * Titre affiché pour indiquer à l'usager ce qui à été recherché. Si la requete était vide
     * lors de l'entrée sur la page, indique un message invitant l'usager à faire ça recherche
     * @returns HTML du titre
     */
    function titre(): ReactElement {
        if (requete) return <h3>{`${TITLE} "${requete}"`}</h3>;

        return <div className="text-center">{FAIRE_RECHERCHE_MESSAGE}</div>;
    }

    /**
     * Indique à l'usager que sa requete n'a pas été fructueuse
     * @returns un message dans un <div>
     */
    function aucunResultat(): ReactElement {
        if (estVide(resultatsRecherche) && requete) {
            return <div>{AUCUN_RESULTAT}</div>;
        }

        return <></>;
    }

    return (
        <>
            {titre()}
            {aucunResultat()}
            <Resultats
                titre="Fournisseurs"
                urlSection="fournisseurs"
                resultats={resultatsRecherche.fournisseurs}
                liens={liens}
            />
            <Resultats
                titre="Activités"
                urlSection="activites"
                resultats={resultatsRecherche.activites}
                liens={liens}
            />
            <Resultats titre="Clients" urlSection="clients" resultats={resultatsRecherche.clients} liens={liens} />
            <Resultats titre="Contacts" urlSection="contacts" resultats={resultatsRecherche.contacts} liens={liens} />
            <Resultats
                titre="Centres de services scolaire"
                urlSection="centres-service-scolaire"
                resultats={resultatsRecherche.centresSS}
                liens={liens}
            />
            <Resultats titre="Lieux" urlSection="lieux" resultats={resultatsRecherche.lieux} liens={liens} />
        </>
    );
}

interface PropsResultats {
    titre: string;
    urlSection: string;
    resultats: BaseEntite[];
    liens: string[];
}

/**
 * Retourne une liste avec les résulats de la recherche. Tous les résulats sont des
 * liens qu'on peut cliquer
 * @param props props des résultats
 * @returns une liste de liens
 */
function Resultats(props: PropsResultats): ReactElement {
    const { titre, urlSection, resultats, liens } = props;

    const fitContent: CSSProperties = {
        maxWidth: 'fit-content',
    };

    /**
     * Callback pour une fonction map(). Pour chaque entité trouvé, créer un lien
     * vers le formulaire correspondant.
     * @param entite trouvé par la requete API
     * @returns des liens cliquables
     */
    function avoirLiens(entite: BaseEntite): ReactElement {
        const position = liens.indexOf(`/dashboard/${urlSection}/${entite.id}`);

        const state: State = {
            pos: position,
            tableauEntite: liens,
        };

        return (
            <Link to={`../${urlSection}/${entite.id}`} key={entite.id} style={fitContent} state={state}>
                <h5>{entite.name}</h5>
            </Link>
        );
    }

    return (
        <>
            {resultats.length > 0 && <h4 className="pt-2">{titre}</h4>}
            <div className="d-flex flex-column">{resultats.map(avoirLiens)}</div>
        </>
    );
}

export default PageRecherche;
